import { useState } from 'react';
import {
  Alert,
  Select,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
} from '@sakhtar/design-system/react';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { ProjectMembersResponse } from '../../lib/types/projects.js';

/**
 * A project's team (prj_project_member). A task inside this project can only
 * be assigned to someone listed here — the API enforces it on create and on
 * assign. Adding/removing is offered only when the API says the caller may
 * manage this project (`canManage`: projects.project.update on this project
 * record); the API re-checks it on every mutation. The project owner cannot
 * be removed, nor can a member who still has open tasks in the project.
 */
export function ProjectTeamPanel({
  slug,
  projectRef,
  projectName,
  onClose,
}: {
  slug: string;
  projectRef: string;
  projectName: string;
  onClose: () => void;
}) {
  const base = tenantBasePath(slug);
  const [refreshKey, setRefreshKey] = useState(0);
  const [candidateRef, setCandidateRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const team = useApiGet<ProjectMembersResponse>(
    `${base}/api/v1/projects/${encodeURIComponent(projectRef)}/members`,
    [projectRef, refreshKey],
  );
  const canManage = team.status === 'ready' && team.data.canManage;
  const candidates = team.status === 'ready' ? team.data.candidates : [];

  async function mutate(path: 'members' | 'members/remove', memberRef: string) {
    setBusy(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/projects/${encodeURIComponent(projectRef)}/${path}`, {
        memberRef,
      });
      setCandidateRef('');
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422 && path === 'members/remove') {
        setError('مالک پروژه و عضوی که هنوز کار باز در این پروژه دارد، قابل حذف نیستند.');
      } else if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
        setError('اجازهٔ مدیریت تیم این پروژه را ندارید.');
      } else {
        setError('ذخیرهٔ تغییر تیم ناموفق بود.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <V2Card>
      <V2CardHeader
        title={`تیم پروژه — ${projectName}`}
        subtitle="کارهای این پروژه فقط به اعضای تیم واگذار می‌شود."
        actions={
          <V2Button variant="ghost" size="sm" onClick={onClose}>
            بستن
          </V2Button>
        }
      />

      {team.status === 'error' ? (
        <div style={{ padding: '0 20px 16px' }}>
          <Alert tone="danger" title="خطا">
            تیم این پروژه قابل نمایش نیست.
          </Alert>
        </div>
      ) : null}

      {team.status === 'ready' ? (
        team.data.members.length === 0 ? (
          <V2EmptyState icon="users" compact title="هنوز عضوی در تیم نیست" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {team.data.members.map((m) => (
              <div
                key={m.memberRef}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--sk-divider)',
                  minHeight: 44,
                }}
              >
                <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{m.displayLabel}</span>
                {m.isOwner ? <V2Badge tone="brand">مالک پروژه</V2Badge> : null}
                {m.isSelf ? <V2Badge tone="neutral">شما</V2Badge> : null}
                {canManage && !m.isOwner ? (
                  <V2Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => void mutate('members/remove', m.memberRef)}
                  >
                    حذف از تیم
                  </V2Button>
                ) : null}
              </div>
            ))}
          </div>
        )
      ) : null}

      {canManage ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '14px 20px',
          }}
        >
          <Select
            aria-label="افزودن همکار به تیم"
            value={candidateRef}
            onChange={(e) => setCandidateRef(e.target.value)}
            disabled={busy || candidates.length === 0}
          >
            <option value="">
              {candidates.length === 0 ? 'همهٔ همکاران در تیم هستند' : 'انتخاب همکار…'}
            </option>
            {candidates.map((m) => (
              <option key={m.memberRef} value={m.memberRef}>
                {m.displayLabel}
              </option>
            ))}
          </Select>
          <V2Button
            variant="primary"
            icon="plus"
            disabled={!candidateRef || busy}
            onClick={() => void mutate('members', candidateRef)}
          >
            افزودن به تیم
          </V2Button>
        </div>
      ) : null}

      {error ? (
        <div style={{ padding: '0 20px 16px' }}>
          <Alert tone="danger" title="خطا">
            {error}
          </Alert>
        </div>
      ) : null}
    </V2Card>
  );
}
